/**
 * Rep Welcome Email — MM Admin section for channel partner disbursement clients.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, RefreshCw } from 'lucide-react';
import { LetterStyleConfigButton } from '../welcome-email/LetterStyleConfigButton';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  disabled: 'bg-red-50 text-red-700',
};

const SECTION_LABELS: Record<string, string> = {
  openingHtml: 'Opening',
  partnershipHtml: 'Partnership / compliance',
  loginHtml: 'Wallet login',
  phoneAlignmentHtml: 'Phone alignment',
  bulletsHtml: 'Wallet features (bullets)',
  supportMmHtml: 'MyMoolah support',
  supportPartnerHtml: 'Partner support',
  closingHtml: 'Closing',
};

const PLACEHOLDERS = ['{{firstName}}', '{{displayName}}', '{{walletUrl}}', '{{partnerSupportEmail}}', '{{mmSupportEmail}}'];

export interface RepWelcomeConfig {
  status: string | null;
  subject: string;
  partnerSupportEmail: string;
  useUploadedLogo: boolean;
  template: Record<string, string>;
  rejectionNote?: string | null;
  hasLogo: boolean;
  logoUrl: string | null;
  displayName: string;
  letterFontFamily?: string;
  letterFontSize?: string;
  letterFontOptions?: string[];
  letterFontSizes?: string[];
}

interface Props {
  clientId: number;
  contactEmail?: string;
  onMessage?: (msg: { type: 'success' | 'error'; text: string }) => void;
}

export const RepWelcomeEmailSection: React.FC<Props> = ({ clientId, contactEmail, onMessage }) => {
  const [config, setConfig] = useState<RepWelcomeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/rep-welcome-email/preview`, {
        params: { firstName: 'Alex' },
        responseType: 'text',
      });
      setPreviewHtml(res.data);
    } catch {
      setPreviewHtml('<p>Preview unavailable</p>');
    } finally {
      setPreviewLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!loading && config) loadPreview();
  }, [loading, config, previewKey, loadPreview]);
  const [testEmail, setTestEmail] = useState(contactEmail || 'support@mymoolah.africa');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showBackfillModal, setShowBackfillModal] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState<{ pendingCount: number; sampleUsers: { email: string }[] } | null>(null);

  const notify = (type: 'success' | 'error', text: string) => {
    onMessage?.({ type, text });
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/rep-welcome-email`);
      setConfig(res.data.data);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Failed to load welcome email config';
      notify('error', String(msg || 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setLogoLoadFailed(false);
    setLogoBlobUrl(null);
    (async () => {
      try {
        const res = await API.get(`/disbursement-clients/${clientId}/rep-welcome-email/logo`, {
          responseType: 'blob',
        });
        if (!res.data || !(res.data instanceof Blob) || !res.data.type.startsWith('image/')) {
          if (!revoked) setLogoLoadFailed(true);
          return;
        }
        objectUrl = URL.createObjectURL(res.data);
        if (!revoked) setLogoBlobUrl(objectUrl);
      } catch {
        if (!revoked) setLogoLoadFailed(true);
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [clientId]);

  const saveDraft = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await API.put(`/disbursement-clients/${clientId}/rep-welcome-email`, {
        subject: config.subject,
        partnerSupportEmail: config.partnerSupportEmail,
        useUploadedLogo: config.useUploadedLogo,
        template: config.template,
        letterFontFamily: config.letterFontFamily,
        letterFontSize: config.letterFontSize,
      });
      setConfig(res.data.data);
      setPreviewKey((k) => k + 1);
      notify('success', 'Draft saved');
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Save failed';
      notify('error', String(msg));
    } finally {
      setSaving(false);
    }
  };

  const postAction = async (path: string, body?: object) => {
    setSaving(true);
    try {
      const res = await API.post(`/disbursement-clients/${clientId}/rep-welcome-email/${path}`, body || {});
      setConfig(res.data.data);
      setPreviewKey((k) => k + 1);
      notify('success', `Action completed: ${path}`);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Action failed';
      notify('error', String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!config) return;
    if (config.status === 'approved') {
      await postAction('disable');
    } else if (config.status === 'disabled') {
      await postAction('enable');
    } else if (config.status === 'pending_approval') {
      await postAction('approve');
    } else {
      notify('error', 'Save draft and submit for approval first, or approve directly as admin.');
    }
  };

  const runTestSend = async () => {
    setSaving(true);
    try {
      await API.post(`/disbursement-clients/${clientId}/rep-welcome-email/test-send`, {
        to: testEmail,
        firstName: 'Alex',
      });
      setShowTestModal(false);
      notify('success', `Test email sent to ${testEmail}`);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Test send failed';
      notify('error', String(msg));
    } finally {
      setSaving(false);
    }
  };

  const openBackfillPreview = async () => {
    try {
      const res = await API.get(`/disbursement-clients/${clientId}/rep-welcome-email/backfill/preview`);
      setBackfillPreview(res.data.data);
      setShowBackfillModal(true);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Backfill preview failed';
      notify('error', String(msg));
    }
  };

  const runBackfill = async () => {
    setSaving(true);
    try {
      const res = await API.post(`/disbursement-clients/${clientId}/rep-welcome-email/backfill`, { confirm: true });
      setShowBackfillModal(false);
      notify('success', `Backfill sent=${res.data.data.sent} failed=${res.data.data.failed}`);
      await loadConfig();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Backfill failed';
      notify('error', String(msg));
    } finally {
      setSaving(false);
    }
  };

  const updateTemplateField = (key: string, value: string) => {
    if (!config) return;
    setConfig({
      ...config,
      template: { ...config.template, [key]: value },
    });
  };

  const insertPlaceholder = (key: string, ph: string) => {
    if (!config) return;
    const current = config.template[key] || '';
    updateTemplateField(key, `${current}${ph}`);
  };

  if (loading) {
    return (
      <div className="mymoolah-card p-8 text-center text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading rep welcome email...
      </div>
    );
  }

  if (!config) return null;

  const isOn = config.status === 'approved';
  const statusLabel = config.status?.replace(/_/g, ' ') || 'not configured';

  return (
    <div className="mymoolah-card overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-[var(--primary)]" />
          <div>
            <h3 className="admin-text-heading text-lg">Rep welcome email</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Sent to wallet users after they register via your partner link
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_BADGE[config.status || 'draft'] || STATUS_BADGE.draft}`}>
            {statusLabel}
          </span>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Send ON</span>
            <button
              type="button"
              role="switch"
              aria-checked={isOn}
              disabled={saving}
              onClick={handleToggle}
              className={`relative w-11 h-6 rounded-full transition-colors ${isOn ? 'bg-emerald-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isOn ? 'translate-x-5' : ''}`} />
            </button>
          </label>
        </div>
      </div>

      {config.rejectionNote && (
        <div className="mx-5 mt-4 p-3 rounded-lg bg-amber-50 border border-amber-100 text-sm text-amber-800">
          <strong>Rejection note:</strong> {config.rejectionNote}
        </div>
      )}

      <div className="p-5 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-3 rounded-lg bg-gray-50 border border-gray-100">
            {logoBlobUrl ? (
              <img src={logoBlobUrl} alt="Partner logo" className="w-16 h-16 object-contain rounded border bg-white" />
            ) : (
              <div className="w-16 h-16 rounded border bg-white flex items-center justify-center text-xs text-gray-400 text-center px-1">
                {logoBlobUrl ? 'Logo' : logoLoadFailed && config.hasLogo ? 'Logo unavailable' : config.hasLogo ? 'Loading…' : 'No logo'}
              </div>
            )}
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-800">Partner logo (read-only)</p>
              <p className="text-xs mt-1">
                {logoBlobUrl
                  ? 'Shown from the uploaded company logo.'
                  : <>Upload <code className="bg-gray-100 px-1 rounded">company_logo</code> in KYB documents below.</>}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Subject override</label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={config.subject}
              onChange={(e) => setConfig({ ...config, subject: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Partner support email</label>
            <input
              type="email"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={config.partnerSupportEmail}
              onChange={(e) => setConfig({ ...config, partnerSupportEmail: e.target.value })}
            />
          </div>

          <div className="flex flex-wrap gap-1 mb-2">
            {PLACEHOLDERS.map((ph) => (
              <span key={ph} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">{ph}</span>
            ))}
          </div>

          {Object.entries(SECTION_LABELS).map(([key, label]) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
                <select
                  className="text-xs border border-gray-200 rounded px-1 py-0.5"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) insertPlaceholder(key, e.target.value);
                    e.target.value = '';
                  }}
                >
                  <option value="">+ placeholder</option>
                  {PLACEHOLDERS.map((ph) => (
                    <option key={ph} value={ph}>{ph}</option>
                  ))}
                </select>
              </div>
              <textarea
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
                value={config.template[key] || ''}
                onChange={(e) => updateTemplateField(key, e.target.value)}
              />
            </div>
          ))}

          <div className="flex flex-wrap gap-2 pt-2">
            <button type="button" disabled={saving} onClick={saveDraft} className="px-4 py-2 text-sm font-semibold rounded-lg bg-[var(--primary)] text-white hover:opacity-90 disabled:opacity-50">
              Save draft
            </button>
            <button type="button" disabled={saving} onClick={() => postAction('submit')} className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 hover:bg-gray-50">
              Submit for approval
            </button>
            <button type="button" disabled={saving} onClick={() => setShowTestModal(true)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 hover:bg-gray-50">
              Test send
            </button>
            <LetterStyleConfigButton
              fontFamily={config.letterFontFamily || 'calibri'}
              fontSize={config.letterFontSize || '11pt'}
              fontOptions={config.letterFontOptions}
              sizeOptions={config.letterFontSizes}
              disabled={saving}
              onApply={async (letterFontFamily, letterFontSize) => {
                setConfig({ ...config, letterFontFamily, letterFontSize });
                setSaving(true);
                try {
                  const res = await API.put(`/disbursement-clients/${clientId}/rep-welcome-email`, {
                    subject: config.subject,
                    partnerSupportEmail: config.partnerSupportEmail,
                    useUploadedLogo: config.useUploadedLogo,
                    template: config.template,
                    letterFontFamily,
                    letterFontSize,
                  });
                  setConfig(res.data.data);
                  setPreviewKey((k) => k + 1);
                  notify('success', 'Letter style saved');
                } catch (err: unknown) {
                  const msg = axios.isAxiosError(err) ? err.response?.data?.error : 'Save failed';
                  notify('error', String(msg));
                } finally {
                  setSaving(false);
                }
              }}
            />
            {config.status === 'pending_approval' && (
              <>
                <button type="button" disabled={saving} onClick={() => postAction('approve')} className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white">
                  Approve
                </button>
                <button type="button" disabled={saving} onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-red-200 text-red-700">
                  Reject
                </button>
              </>
            )}
            {config.status === 'approved' && (
              <button type="button" disabled={saving} onClick={openBackfillPreview} className="px-4 py-2 text-sm font-semibold rounded-lg border border-indigo-200 text-indigo-700">
                Backfill existing reps
              </button>
            )}
            <button type="button" onClick={() => setPreviewKey((k) => k + 1)} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-800">
              Refresh preview
            </button>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Preview (sample: Alex)</p>
          {previewLoading ? (
            <div className="min-h-[520px] flex items-center justify-center border border-gray-200 rounded-lg text-gray-400">Loading preview...</div>
          ) : (
            <iframe
              title="Rep welcome email preview"
              srcDoc={previewHtml}
              className="w-full min-h-[520px] border border-gray-200 rounded-lg bg-white"
              sandbox=""
            />
          )}
        </div>
      </div>

      {showTestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h4 className="font-semibold text-lg">Send test email</h4>
            <input
              type="email"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowTestModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button type="button" disabled={saving} onClick={runTestSend} className="px-4 py-2 text-sm bg-[var(--primary)] text-white rounded-lg">Send</button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h4 className="font-semibold text-lg">Reject template</h4>
            <textarea
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Note for the channel partner..."
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  await postAction('reject', { note: rejectNote });
                  setShowRejectModal(false);
                  setRejectNote('');
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {showBackfillModal && backfillPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h4 className="font-semibold text-lg">Backfill welcome emails</h4>
            <p className="text-sm text-gray-600">
              This will send the welcome email to <strong>{backfillPreview.pendingCount}</strong> attributed users who have not been recorded in a prior backfill.
            </p>
            {backfillPreview.sampleUsers?.length > 0 && (
              <ul className="text-xs text-gray-500 list-disc pl-4">
                {backfillPreview.sampleUsers.map((u, i) => (
                  <li key={i}>{u.email}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowBackfillModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button type="button" disabled={saving} onClick={runBackfill} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg">
                Confirm send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepWelcomeEmailSection;
